import React, { useState, useEffect, useCallback } from 'react';
import { Tv } from 'lucide-react';
import {
  isControl4Authenticated,
  authenticateControl4,
  ensureAuthenticated,
  executeCommand,
  getItemVariables,
  clearControl4Auth,
} from '../services/control4';
import { getCurrentUser } from '../services/auth';

// Office TV device IDs from Control4 (type 7 source proxies)
// Tv1=521, Tv2=564, Tv3=525, Tv4=547, Tv5=549, Tv6=531
const OFFICE_TVS = [521, 564, 525, 547, 549, 531];

// TVs that should switch to HDMI 2 (binding 1022) on power on
const HDMI2_TVS = [521, 547]; // Tv1 and Tv4

const POLL_INTERVAL = 20 * 60 * 1000; // 20 minutes

export default function Control4TvToggle() {
  const [tvOn, setTvOn] = useState(null); // null = unknown, true/false = known
  const [loading, setLoading] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [error, setError] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const checkTvStates = useCallback(async () => {
    // Auto-re-auth if token expired but we have stored credentials
    const authed = await ensureAuthenticated();
    if (!authed) return;
    try {
      const results = await Promise.allSettled(
        OFFICE_TVS.map(id => getItemVariables(id, 'POWER_STATE'))
      );
      let anyOn = false;
      for (const r of results) {
        if (r.status === 'fulfilled') {
          const val = r.value;
          const powerState = Array.isArray(val) ? val[0]?.value : (val?.POWER_STATE ?? val?.value);
          if (Number(powerState) === 1) { anyOn = true; break; }
        }
      }
      setTvOn(anyOn);
    } catch (err) {
      // Silently fail — don't disrupt the UI
    }
  }, []);

  // Poll TV states every 20 minutes (checkTvStates handles re-auth internally)
  useEffect(() => {
    checkTvStates();
    const interval = setInterval(checkTvStates, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [checkTvStates]);

  async function handleLogin(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await authenticateControl4(username, password);
      setShowLogin(false);
      setPassword('');
      checkTvStates(); // immediately poll state after login
    } catch (err) {
      console.error('Control4 auth error:', err);
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  }

  async function toggleTv() {
    // Try auto-re-auth first, show login only if no stored credentials
    const authed = await ensureAuthenticated();
    if (!authed) {
      setShowLogin(true);
      return;
    }

    const command = tvOn ? 'OFF' : 'ON';
    setLoading(true);
    setError('');
    try {
      await Promise.allSettled(
        OFFICE_TVS.map(id => executeCommand(id, command))
      );

      // After turning on, switch Tv1 and Tv4 to HDMI 2
      if (command === 'ON') {
        // Small delay to let TVs boot before switching input
        setTimeout(() => {
          Promise.allSettled(
            HDMI2_TVS.map(id => executeCommand(id, 'SET_INPUT', { INPUT: 1022 }))
          );
        }, 3000);
      }

      setTvOn(!tvOn);
    } catch (err) {
      console.error('Control4 command error:', err);
      setError(err.message || 'Command failed');
    } finally {
      setLoading(false);
    }
  }

  // Visible on localhost (netlify dev) OR for Greg (admin_id=1) on production.
  // Other admins don't see the button — TV control is single-user.
  const isLocal = window.location.hostname.includes('localhost') || window.location.hostname.includes('127.0.0.1');
  const user = getCurrentUser();
  const isGreg = user?.id === 1;
  if (!isLocal && !isGreg) return null;

  return (
    <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
      <button
        onClick={toggleTv}
        disabled={loading}
        title={tvOn === null ? 'TVs' : tvOn ? 'Turn all TVs off' : 'Turn all TVs on'}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          background: tvOn === true ? 'var(--navy)' : 'var(--gray-100)',
          color: tvOn === true ? '#fff' : 'var(--gray-500)',
          border: `1px solid ${tvOn === true ? 'var(--navy)' : 'var(--gray-300)'}`,
          borderRadius: 6,
          padding: '4px 10px',
          cursor: loading ? 'wait' : 'pointer',
          fontSize: 11,
          fontWeight: 600,
          fontFamily: 'Montserrat, sans-serif',
          transition: 'all 0.2s',
          opacity: loading ? 0.6 : 1,
          whiteSpace: 'nowrap',
        }}
      >
        <Tv size={13} />
        {tvOn === null ? 'TVs' : tvOn ? 'TVs OFF' : 'TVs ON'}
      </button>

      {showLogin && (
        <div style={{
          position: 'absolute',
          top: '100%',
          right: 0,
          marginTop: 8,
          background: '#fff',
          border: '1px solid var(--gray-200)',
          borderRadius: 10,
          padding: 16,
          boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
          zIndex: 1000,
          width: 260,
        }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--navy)', marginBottom: 10 }}>
            Control4 Login
          </div>
          <form onSubmit={handleLogin}>
            <input
              type="email"
              placeholder="Control4 email"
              value={username}
              onChange={e => setUsername(e.target.value)}
              style={{ width: '100%', padding: '6px 10px', fontSize: 12, border: '1px solid var(--gray-300)', borderRadius: 6, marginBottom: 8, boxSizing: 'border-box' }}
              required
            />
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              style={{ width: '100%', padding: '6px 10px', fontSize: 12, border: '1px solid var(--gray-300)', borderRadius: 6, marginBottom: 10, boxSizing: 'border-box' }}
              required
            />
            {error && <div style={{ color: '#c00', fontSize: 11, marginBottom: 8 }}>{error}</div>}
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="submit" disabled={loading} className="btn btn-navy" style={{ flex: 1, fontSize: 11, padding: '6px 0' }}>
                {loading ? 'Connecting...' : 'Connect'}
              </button>
              <button type="button" onClick={() => setShowLogin(false)} style={{ flex: 1, fontSize: 11, padding: '6px 0', background: 'var(--gray-100)', border: '1px solid var(--gray-300)', borderRadius: 6, cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
